import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useTransition, useSpring } from 'react-spring';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import easing from '../../../../../lib/easing/easing';
import randomIntBetween from '../../../../../lib/randomInBetween/randomInBetween';
import Outer from './styled/Outer';
import Ring from './styled/Ring';
import Dot from './components/Dot/Dot';
import Arrow from './components/Arrow/Arrow';

function useSpringCompat(config) {
  const prevConfigRef = useRef();

  const prevConfig = prevConfigRef.current;

  const [anim, set] = useSpring(() => {
    return config.from || {};
  });

  useEffect(() => {
    const same = isEqual(prevConfig, config);

    if (!same) {
      if (isArray(config.to)) {
        set(config);
      } else {
        set(config);
      }
    }

    prevConfigRef.current = config;
  });

  return anim;
}

function useRingAnimations({
  largestRingDiameterPx,
  status,
  ringSpacePx,
  totalRings,
  ref,
}) {
  let delay = 0;

  if (status === 'expand') {
    delay = 200;
  } else if (status === 'dropped') {
    delay = 200;
  } else {
    delay = 200;
  }

  const ringConfigs = Array(totalRings)
    .fill()
    .map((unused, i) => {
      const ringDiameter = largestRingDiameterPx - i * ringSpacePx;

      if (ringDiameter < 0) {
        return null;
      }

      return {
        i, ringDiameter,
      };
    })
    .filter((item) => item)
    .reverse();

  const ringAnims = useTransition(ringConfigs, ({ i }) => i, {
    delay,
    enter: ({ ringDiameter }) => {
      return {
        height: `${ringDiameter}px`,
        opacity: 1,
        width: `${ringDiameter}px`,
      };
    },
    from: () => {
      return {
        height: '0px',
        opacity: 0,
        width: '0px',
      };
    },
    leave: () => {
      return {
        height: '0px',
        opacity: 0,
        width: '0px',
      };
    },
    ref,
    trail: 50,
    unique: true,
  });

  return ringAnims;
}

function useDotAnimations({
  status, totalDots, maxDistance, ref,
}) {
  let delay = 0;

  if (status === 'expand') {
    delay = 0;
  } else if (status === 'dropped') {
    delay = 0;
  } else {
    delay = 0;
  }

  const [dotsState, setDotsState] = useState([]);

  useEffect(() => {
    const separationInterval = maxDistance / totalDots;

    const dotsConfig = Array(totalDots)
      .fill()
      .map((unused, i) => {
        let distancePx;

        let angleDegrees;

        let size;

        let singleRevolutionDurMs;

        if (!dotsState[i]) {
          distancePx = randomIntBetween(
            i * separationInterval + 100,
            Math.min((i + 1) * separationInterval, maxDistance),
          );
          angleDegrees = randomIntBetween(0, 259);
          size = randomIntBetween(5, 12);
          singleRevolutionDurMs = (i + 1) * 2500 + randomIntBetween(-50, 50);
        } else {
          ;({
            distancePx,
            angleDegrees,
            size,
            singleRevolutionDurMs,
          } = dotsState[i]);
        }
        distancePx = distancePx > maxDistance ? maxDistance : distancePx;

        return {
          angleDegrees, distancePx, i, singleRevolutionDurMs, size,
        };
      })
      .filter((item) => item);

    setDotsState(dotsConfig);
  }, [totalDots, maxDistance]);

  const dotAnims = useTransition(dotsState, ({ i }) => i, {
    delay,
    enter: ({
      distancePx, angleDegrees, size,
    }) => {
      return {
        angleDegrees,
        distancePx,
        opacity: 1,
        size,
      };
    },
    from: ({ angleDegrees, size }) => {
      return {
        angleDegrees,
        distancePx: 0,
        opacity: 0,
        size,
      };
    },
    leave: ({ angleDegrees, size }) => {
      return {
        angleDegrees,
        distancePx: 0,
        opacity: 0,
        size,
      };
    },
    ref,
    trail: 50,
    unique: true,
  });

  return dotAnims;
}

function useOuterAnimation({ status, onDestroyed = null }) {
  let visible;

  let delay = 0;

  let onRest;

  if (status === 'expand') {
    visible = true;
  } else if (status === 'dropped') {
    visible = false;
    delay = 1050;
    onRest = onDestroyed;
  } else {
    visible = false;
    delay = 200;
    onRest = onDestroyed;
  }

  const [outerAnim, set] = useSpring(() => {
    return {
      opacity: 0,
    };
  });

  useEffect(() => {
    set({
      delay,
      onRest: ({ opacity }) => {
        if (opacity === 0) {
          onRest && onRest();
        }
      },
      opacity: visible ? 1 : 0,
    });
  }, [visible, delay, onRest]);

  return outerAnim;
}

function useArrowAnimation({ status, ref }) {
  let configs = [];

  let delay = 0;

  if (status === 'expand') {
    configs = [
      {
        config: {
          duration: 500,
          easing: easing.easeOutQuart,
        },
        opacity: 1,
        transform: 'translateY(0px)',
      },
    ];
  } else if (status === 'dropped') {
    delay = 100;
    configs = [
      {
        config: {
          duration: 700,
          easing: easing.easeOutQuad,
        },
        opacity: 1,
        transform: 'translateY(50px)',
      },
      {
        config: {
          duration: 500,
          easing: easing.easeInQuart,
        },
        opacity: 0,
        transform: 'translateY(-200px)',
      },
    ];
  } else {
    delay = 100;
    configs = [
      {
        config: {
          duration: 500,
          easing: easing.easeOutQuart,
        },
        opacity: 0,
        transform: 'translateY(20px)',
      },
    ];
  }

  const arrowAnim = useSpringCompat({
    delay,
    ref,
    to: configs,
  });

  return arrowAnim;
}

export default function GalaxyAnimation({ status, onReadyToRemove }) {
  const largestRingDiameterPx = 870;

  const ringSpacePx = 200;

  // const outerAnimRef = useRef()
  // const ringAnimsRef = useRef()
  // const dotAnimsRef = useRef()
  // const arrowAnimRef = useRef()

  let totalDots;

  let totalRings;

  let onOuterDestroyed;

  const hideAndDestroy = useCallback(() => {
    onReadyToRemove && onReadyToRemove();
  }, [onReadyToRemove]);

  if (status === 'expand') {
    totalDots = 8;
    totalRings = 4;
  } else if (status === 'dropped') {
    totalDots = 0;
    totalRings = 0;
    onOuterDestroyed = hideAndDestroy;
  } else {
    totalDots = 0;
    totalRings = 0;
    onOuterDestroyed = hideAndDestroy;
  }

  const outerAnim = useOuterAnimation({
    onDestroyed: onOuterDestroyed,
    status,
    // ref: outerAnimRef
  });

  const ringAnims = useRingAnimations({
    largestRingDiameterPx,
    ringSpacePx,
    status,
    totalRings,
    // ref: ringAnimsRef
  });

  const dotAnims = useDotAnimations({
    maxDistance: largestRingDiameterPx / 2,
    status,
    totalDots,
    // ref: dotAnimsRef
  });

  const arrowAnim = useArrowAnimation({
    status,
    // ref: arrowAnimRef
  });

  return (
    <>
      <Outer style={outerAnim}>
        {ringAnims.map(
          ({
            item, key, props,
          }) => item && (
            <Ring
              key={key}
              style={props}
            />
          ),
        )}
        {dotAnims.map(
          ({
            item, key, props,
          }) =>
            item && (
              <Dot
                key={key}
                item={item}
                {...props}
              />
            ),
        )}
        <Arrow {...arrowAnim} />
      </Outer>
    </>
  );
};
